import { apiClient } from '@/api/apiClient'
import { ElectricityProductionMethod } from '@/common'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export enum SupplierEmissionsReportEvidenceType {
  TED01 = 'TED01',
  TED02 = 'TED02',
  TED03 = 'TED03',
  TED04 = 'TED04',
  TED05 = 'TED05'
}

export type SupplierEmissionsReport = {
  id: string
  periodId: string
  goods: {
    productionMethodCode: string
    cnCode: string
    goodsDescription: string
  }[]
  specificDirectEmbeddedEmissions: number
  specificIndirectEmbeddedEmissions: number
  electricityProductionMethod: ElectricityProductionMethod
  electricityConsumptionMWh: number
  evidenceFileName?: string
  evidenceType?: SupplierEmissionsReportEvidenceType
  installationId: string
  linkedImportedGoodsCount?: number
  createdAt: string
  updatedAt: string
}

type CreateSupplierEmissionsReportPayload = {
  periodId: string
  goods: {
    productionMethodCode: string
    cnCode: string
    goodsDescription: string
  }[]
  specificDirectEmbeddedEmissions: number
  specificIndirectEmbeddedEmissions: number
  electricityProductionMethod: ElectricityProductionMethod
  electricityConsumptionMWh: number
  evidenceFileName?: string
  evidenceType?: SupplierEmissionsReportEvidenceType
}

type UpdateSupplierEmissionsReportPayload = CreateSupplierEmissionsReportPayload

const queryKeyEmissionsReports = 'emissionsReports'

export const useGetAllSupplierEmissionsReportsForOrganization = (
  organizationId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [queryKeyEmissionsReports, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<SupplierEmissionsReport[]>(
        `/v2/organizations/${organizationId}/supplier-emissions-reports`
      )
      return response.data
    },
    enabled: options?.enabled ?? true
  })
}

export const useGetAllSupplierEmissionsReportsForInstallation = (
  partnershipId: string,
  installationId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [queryKeyEmissionsReports, partnershipId, installationId],
    queryFn: async () => {
      const response = await apiClient.get<SupplierEmissionsReport[]>(
        `/v2/partnerships/${partnershipId}/installations/${installationId}/supplier-emissions-reports`
      )
      return response.data
    },
    enabled: options?.enabled ?? true
  })
}

export const useCreateSupplierEmissionsReport = (partnershipId: string, installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyEmissionsReports, partnershipId, installationId],
    mutationFn: async (payload: CreateSupplierEmissionsReportPayload) => {
      const response = await apiClient.post<SupplierEmissionsReport>(
        `/v2/partnerships/${partnershipId}/installations/${installationId}/supplier-emissions-reports`,
        payload
      )

      queryClient.setQueryData<SupplierEmissionsReport[]>(
        [queryKeyEmissionsReports, partnershipId, installationId],
        oldData => [...(oldData || []), response.data]
      )

      return response.data
    }
  })
}

export const useUpdateSupplierEmissionsReport = (partnershipId: string, installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [queryKeyEmissionsReports, partnershipId, installationId],
    mutationFn: async ({
      reportId,
      payload
    }: {
      reportId: string
      payload: UpdateSupplierEmissionsReportPayload
    }) => {
      const response = await apiClient.put<SupplierEmissionsReport>(
        `/v2/partnerships/${partnershipId}/installations/${installationId}/supplier-emissions-reports/${reportId}`,
        payload
      )

      queryClient.setQueryData<SupplierEmissionsReport[]>(
        [queryKeyEmissionsReports, partnershipId, installationId],
        oldData =>
          oldData?.map(emissionsReport =>
            emissionsReport.id === response.data.id
              ? {
                  ...response.data,
                  linkedImportedGoodsCount: response.data.linkedImportedGoodsCount ?? emissionsReport.linkedImportedGoodsCount
                }
              : emissionsReport
          ) || [response.data]
      )

      return response.data
    }
  })
}

export const useDeleteSupplierEmissionsReport = (
  partnershipId: string,
  installationId: string,
  supplierEmissionsReportId: string
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(
        `/v2/partnerships/${partnershipId}/installations/${installationId}/supplier-emissions-reports/${supplierEmissionsReportId}`
      )

      queryClient.setQueryData<SupplierEmissionsReport[]>(
        [queryKeyEmissionsReports, partnershipId, installationId],
        oldData =>
          oldData?.filter(report => report.id !== supplierEmissionsReportId) || []
      )
    }
  })
}
